import { makeAutoObservable, runInAction } from 'mobx';
import { OtaUpdateStatus } from '~/services/ota/types';
import type {
  OtaRelease,
  OtaCheckResponse,
  OtaStatusResponse,
  UpdateAvailableCallback,
  DownloadProgressCallback,
  UpdateCompleteCallback,
  UpdateFailedCallback,
} from '~/services/ota/types';

/**
 * OTA Service Interface
 * This interface defines the contract between the OtaStore and OtaService.
 */
export interface IOtaService {
  // Lifecycle
  init(): void;
  startChecking(): void;
  stopChecking(): void;

  // Check for updates
  checkForUpdate(): Promise<OtaCheckResponse>;

  // Download & Install
  downloadUpdate(release: OtaRelease): Promise<void>;
  installUpdate(bundlePath: string): Promise<void>;

  // Status reporting
  reportStatus(
    status: OtaUpdateStatus,
    progress?: number,
    error?: string,
  ): Promise<OtaStatusResponse>;

  // Listeners
  onUpdateAvailable(callback: UpdateAvailableCallback): void;
  onDownloadProgress(callback: DownloadProgressCallback): void;
  onUpdateComplete(callback: UpdateCompleteCallback): void;
  onUpdateFailed(callback: UpdateFailedCallback): void;
}

const LAST_CHECK_STORAGE_KEY = 'efflux_ota_last_check';

/**
 * OtaStore - MobX Store for OTA Update Management
 *
 * Manages state for Over-The-Air updates:
 * - Tracks update availability and download progress
 * - Integrates with OtaService for update operations
 * - Persists last check time to localStorage
 * - Provides UI-friendly computed values
 *
 * @example
 * ```ts
 * // Initialize with OtaService
 * otaStore.init(otaService);
 *
 * // Check for updates
 * await otaStore.checkForUpdate();
 *
 * // Download update if available
 * if (otaStore.updateAvailable) {
 *   await otaStore.downloadUpdate();
 * }
 * ```
 */
class OtaStore {
  // ===========================================================================
  // OBSERVABLE STATE
  // ===========================================================================

  /** Whether an update is available for download */
  updateAvailable: boolean = false;

  /** Information about the available release */
  currentRelease: OtaRelease | null = null;

  /** Current status of the update process */
  updateStatus: OtaUpdateStatus | 'idle' | 'checking' = 'idle';

  /** Download progress (0-100) */
  downloadProgress: number = 0;

  /** Error message if update fails */
  error: string | null = null;

  /** Current application version (from package.json) */
  currentVersion: string = '1.0.0';

  /** Whether currently checking for updates */
  isChecking: boolean = false;

  /** Last time updates were checked (ISO timestamp) */
  lastCheckTime: string | null = null;

  // ===========================================================================
  // INTERNAL
  // ===========================================================================

  /** Reference to the OTA service instance */
  private otaService: IOtaService | null = null;

  constructor() {
    makeAutoObservable(this);
    this.loadCurrentVersion();
    this.loadLastCheckTime();
  }

  // ===========================================================================
  // COMPUTED VALUES
  // ===========================================================================

  /**
   * Whether an update is currently being downloaded or installed.
   */
  get isUpdating(): boolean {
    return (
      this.updateStatus === OtaUpdateStatus.DOWNLOADING ||
      this.updateStatus === OtaUpdateStatus.INSTALLING
    );
  }

  /**
   * Human-readable label for the current update status.
   */
  get updateStatusLabel(): string {
    switch (this.updateStatus) {
      case 'idle':
        return 'No updates';
      case 'checking':
        return 'Checking for updates...';
      case OtaUpdateStatus.PENDING:
        return 'Update available';
      case OtaUpdateStatus.DOWNLOADING:
        return 'Downloading update...';
      case OtaUpdateStatus.DOWNLOADED:
        return 'Download complete';
      case OtaUpdateStatus.INSTALLING:
        return 'Installing update...';
      case OtaUpdateStatus.SUCCESS:
        return 'Update successful';
      case OtaUpdateStatus.FAILED:
        return 'Update failed';
      case OtaUpdateStatus.ROLLED_BACK:
        return 'Update rolled back';
      default:
        return 'Unknown status';
    }
  }

  /**
   * Whether a failed update can be retried.
   */
  get canRetry(): boolean {
    return (
      this.updateStatus === OtaUpdateStatus.FAILED &&
      this.currentRelease !== null
    );
  }

  /**
   * Formatted download progress as percentage string.
   * @example "45%"
   */
  get progressPercentage(): string {
    return `${Math.round(this.downloadProgress)}%`;
  }

  /**
   * Whether the store is initialized with an OTA service.
   */
  get isInitialized(): boolean {
    return this.otaService !== null;
  }

  /**
   * Formatted last check time.
   * @example "2:30:45 PM"
   */
  get lastCheckTimeFormatted(): string {
    if (!this.lastCheckTime) return 'Never';
    try {
      return new Date(this.lastCheckTime).toLocaleTimeString();
    } catch {
      return this.lastCheckTime;
    }
  }

  // ===========================================================================
  // INITIALIZATION
  // ===========================================================================

  /**
   * Initialize the OTA store with the OTA service.
   * Registers callbacks to receive update notifications.
   *
   * @param otaService - The OTA service instance to integrate with
   */
  init(otaService: IOtaService): void {
    if (this.otaService) {
      console.warn('[OtaStore] Already initialized');
      return;
    }

    this.otaService = otaService;

    // Register callbacks with OTA service
    this.otaService.onUpdateAvailable((release) => {
      this.setUpdateAvailable(release);
    });

    this.otaService.onDownloadProgress((progress) => {
      this.setDownloadProgress(progress);
    });

    this.otaService.onUpdateComplete(() => {
      runInAction(() => {
        this.updateStatus = OtaUpdateStatus.SUCCESS;
        this.error = null;
      });
    });

    this.otaService.onUpdateFailed((error) => {
      this.setError(error.message);
    });

    console.log('[OtaStore] Initialized with OTA service', {
      currentVersion: this.currentVersion,
    });
  }

  /**
   * Load current version from package.json via import.meta.env.
   * Falls back to '1.0.0' if not available.
   */
  private loadCurrentVersion(): void {
    try {
      // Version is set at build time via vite
      const version = import.meta.env.VITE_APP_VERSION || '1.0.0';
      runInAction(() => {
        this.currentVersion = version;
      });
    } catch (error) {
      console.warn('[OtaStore] Could not load version from env, using default');
    }
  }

  /**
   * Load last check time from localStorage.
   */
  private loadLastCheckTime(): void {
    const stored = localStorage.getItem(LAST_CHECK_STORAGE_KEY);
    if (stored) {
      runInAction(() => {
        this.lastCheckTime = stored;
      });
    }
  }

  /**
   * Persist last check time to localStorage.
   */
  private saveLastCheckTime(): void {
    if (this.lastCheckTime) {
      localStorage.setItem(LAST_CHECK_STORAGE_KEY, this.lastCheckTime);
    }
  }

  // ===========================================================================
  // ACTIONS
  // ===========================================================================

  /**
   * Set update available state with release information.
   * Automatically updates status to 'pending'.
   *
   * @param release - The OTA release information
   */
  setUpdateAvailable(release: OtaRelease): void {
    runInAction(() => {
      this.updateAvailable = true;
      this.currentRelease = release;
      this.updateStatus = OtaUpdateStatus.PENDING;
      this.error = null;
    });

    console.log('[OtaStore] Update available:', {
      version: release.version,
      currentVersion: this.currentVersion,
      isMandatory: release.isMandatory,
    });
  }

  /**
   * Update download progress.
   *
   * @param progress - Progress value (0-100)
   */
  setDownloadProgress(progress: number): void {
    runInAction(() => {
      this.downloadProgress = Math.min(100, Math.max(0, progress));
      if (this.updateStatus !== OtaUpdateStatus.DOWNLOADING) {
        this.updateStatus = OtaUpdateStatus.DOWNLOADING;
      }
    });
  }

  /**
   * Set update status.
   *
   * @param status - The new update status
   */
  setUpdateStatus(status: OtaUpdateStatus | 'idle' | 'checking'): void {
    runInAction(() => {
      this.updateStatus = status;
      if (status === 'idle' || status === OtaUpdateStatus.SUCCESS) {
        this.error = null;
      }
    });
  }

  /**
   * Set error message and mark update as failed.
   *
   * @param error - The error message
   */
  setError(error: string): void {
    runInAction(() => {
      this.error = error;
      this.updateStatus = OtaUpdateStatus.FAILED;
    });

    console.error('[OtaStore] Update error:', error);
  }

  /**
   * Clear update state (after successful update or user dismissal).
   */
  clearUpdate(): void {
    runInAction(() => {
      this.updateAvailable = false;
      this.currentRelease = null;
      this.updateStatus = 'idle';
      this.downloadProgress = 0;
      this.error = null;
    });

    console.log('[OtaStore] Update state cleared');
  }

  /**
   * Check for available updates.
   * Updates lastCheckTime on completion.
   */
  async checkForUpdate(): Promise<void> {
    if (!this.otaService) {
      console.error('[OtaStore] Not initialized');
      return;
    }

    runInAction(() => {
      this.isChecking = true;
      this.updateStatus = 'checking';
      this.error = null;
    });

    try {
      const result = await this.otaService.checkForUpdate();

      runInAction(() => {
        this.isChecking = false;
        this.lastCheckTime = new Date().toISOString();
        this.saveLastCheckTime();

        if (result.update_available && result.release) {
          // Transform DTO to client model (snake_case to camelCase)
          const release: OtaRelease = {
            id: result.release.id,
            version: result.release.version,
            buildNumber: result.release.build_number,
            platform: result.release.platform,
            bundleUrl: result.release.bundle_url,
            bundleChecksum: result.release.bundle_checksum,
            bundleSizeBytes: result.release.bundle_size_bytes,
            isMandatory: result.release.is_mandatory,
            minSupportedVersion: result.release.min_supported_version,
            releaseNotes: result.release.release_notes,
            releasedAt: result.release.released_at,
          };
          this.setUpdateAvailable(release);
        } else {
          this.updateStatus = 'idle';
        }
      });

      console.log('[OtaStore] Check complete:', {
        updateAvailable: result.update_available,
        version: result.release?.version,
      });
    } catch (error: unknown) {
      console.error('[OtaStore] Check failed:', error);
      runInAction(() => {
        this.isChecking = false;
        const msg = error instanceof Error ? error.message : String(error);
        this.setError(msg || 'Failed to check for updates');
      });
    }
  }

  /**
   * Download the current available update.
   * Requires an update to be available (currentRelease must be set).
   */
  async downloadUpdate(): Promise<void> {
    if (!this.otaService) {
      console.error('[OtaStore] Not initialized');
      return;
    }

    if (!this.currentRelease) {
      console.error('[OtaStore] No update available to download');
      return;
    }

    runInAction(() => {
      this.updateStatus = OtaUpdateStatus.DOWNLOADING;
      this.downloadProgress = 0;
      this.error = null;
    });

    try {
      await this.otaService.downloadUpdate(this.currentRelease);

      runInAction(() => {
        this.updateStatus = OtaUpdateStatus.DOWNLOADED;
        this.downloadProgress = 100;
      });

      console.log('[OtaStore] Download complete');
    } catch (error: unknown) {
      console.error('[OtaStore] Download failed:', error);
      const msg = error instanceof Error ? error.message : String(error);
      this.setError(msg || 'Download failed');
    }
  }

  /**
   * Retry a failed update.
   * Resets error state and attempts download again.
   */
  async retryUpdate(): Promise<void> {
    if (!this.canRetry) {
      console.warn(
        '[OtaStore] Cannot retry - no failed update or no release available',
      );
      return;
    }

    runInAction(() => {
      this.error = null;
      this.updateStatus = OtaUpdateStatus.PENDING;
      this.downloadProgress = 0;
    });

    console.log('[OtaStore] Retrying update');
    await this.downloadUpdate();
  }

  // ===========================================================================
  // DEBUG
  // ===========================================================================

  /**
   * Debug helper - logs current state to console.
   */
  debugState(): void {
    console.group('[OtaStore] Debug State');
    console.log('Current version:', this.currentVersion);
    console.log('Update available:', this.updateAvailable);
    console.log('Status:', this.updateStatus);
    console.log('Release:', this.currentRelease);
    console.log('Progress:', this.progressPercentage);
    console.log('Error:', this.error);
    console.log('Last check:', this.lastCheckTimeFormatted);
    console.groupEnd();
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  /**
   * Reset the store to initial state.
   * Should be called when cleaning up or logging out.
   */
  reset(): void {
    if (this.otaService) {
      this.otaService.stopChecking();
      this.otaService = null;
    }

    runInAction(() => {
      this.updateAvailable = false;
      this.currentRelease = null;
      this.updateStatus = 'idle';
      this.downloadProgress = 0;
      this.error = null;
      this.isChecking = false;
      // Don't reset currentVersion or lastCheckTime on reset
    });

    console.log('[OtaStore] Reset complete');
  }
}

export default OtaStore;
